import type { WorkspaceTable } from '../services/workspace-assistant.types';

/** A table's chart as plain data (bars or pie slices) — drawn on screen (WorkspaceChart) and in PDFs (chartSvg). */
export type ChartItem = { label: string; value: number; compare?: number; tone?: 'good' | 'warn' | 'bad' };
export type ChartData = { type: 'bar' | 'pie'; title: string; items: ChartItem[]; valueLabel?: string; compareLabel?: string; max: number };

const MAX_BARS = 12;
export const CHART_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#64748b'];
export const TONE_COLORS = { good: '#16a34a', warn: '#f59e0b', bad: '#dc2626' } as const;
const toNumber = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() && Number.isFinite(Number(v)) ? Number(v) : undefined);

export function chartData(table: Pick<WorkspaceTable, 'chart' | 'rows' | 'columns' | 'title'>): ChartData | undefined {
  const c = table.chart;
  if (!c) return undefined;
  const label = (key?: string) => table.columns.find(col => col.key === key)?.label;
  let items: ChartItem[];
  if (c.points?.length) items = c.points.filter(p => p.value > 0).map(p => ({ label: p.label, value: p.value, ...(p.tone ? { tone: p.tone } : {}) }));
  else {
    if (!c.label || !c.value) return undefined;
    items = table.rows.flatMap(r => {
      const value = toNumber(r.cells[c.value!]);
      if (value === undefined) return [];
      const compare = c.compare ? toNumber(r.cells[c.compare]) : undefined;
      return [{ label: String(r.cells[c.label!] ?? '').slice(0, 60) || '—', value, ...(compare !== undefined ? { compare } : {}) }];
    }).slice(0, MAX_BARS);
  }
  if (items.length < (c.type === 'pie' ? 2 : 1) || items.every(i => !i.value && !i.compare)) return undefined;
  const max = Math.max(...items.map(i => Math.max(i.value, i.compare ?? 0)), 0);
  return { type: c.type, title: c.title || table.title, items, valueLabel: label(c.value), compareLabel: label(c.compare), max };
}

/** Pie slices: start / end angles in radians, from 12 o'clock clockwise. */
export function pieSlices(items: ChartItem[]) {
  const total = items.reduce((n, i) => n + i.value, 0) || 1;
  let angle = 0;
  return items.map((item, i) => {
    const start = angle, sweep = (item.value / total) * Math.PI * 2;
    angle += sweep;
    return { ...item, start, end: start + sweep, percent: Math.round((item.value / total) * 1000) / 10, color: item.tone ? TONE_COLORS[item.tone] : CHART_COLORS[i % CHART_COLORS.length] };
  });
}

/** SVG path of a donut slice centred at (c, c). */
export function arcPath(c: number, r: number, inner: number, start: number, end: number) {
  if (end - start >= Math.PI * 2 - 1e-6) end = start + Math.PI * 2 - 1e-4;
  const p = (radius: number, a: number) => `${(c + radius * Math.sin(a)).toFixed(2)} ${(c - radius * Math.cos(a)).toFixed(2)}`;
  const large = end - start > Math.PI ? 1 : 0;
  return `M ${p(r, start)} A ${r} ${r} 0 ${large} 1 ${p(r, end)} L ${p(inner, end)} A ${inner} ${inner} 0 ${large} 0 ${p(inner, start)} Z`;
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The chart as standalone SVG markup (for PDFs and printing). */
export function chartSvg(data: ChartData, format: (n: number) => string, rtl = false): string {
  const W = 640;
  if (data.type === 'pie') {
    const slices = pieSlices(data.items), size = 180, c = size / 2, x0 = rtl ? W - size - 10 : 10;
    // Legend beside the pie: right-aligned to its left in Arabic, left-aligned to its right otherwise.
    const lx = rtl ? x0 - 24 : x0 + size + 24;
    const legend = slices.map((s, i) => { const y = 28 + i * 24;
      return `<rect x="${rtl ? lx - 12 : lx}" y="${y - 10}" width="12" height="12" rx="3" fill="${s.color}"/><text x="${rtl ? lx - 18 : lx + 18}" y="${y}" font-size="13" text-anchor="${rtl ? 'end' : 'start'}">${esc(s.label)} — ${esc(format(s.value))} (${s.percent}%)</text>`; }).join('');
    const h = Math.max(size, 28 + slices.length * 24) + 10;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${h}" viewBox="0 0 ${W} ${h}"><g transform="translate(${x0} 5)">${slices.map(s => `<path d="${arcPath(c, c, c * 0.55, s.start, s.end)}" fill="${s.color}"/>`).join('')}</g>${legend}</svg>`;
  }
  const rowH = data.items.some(i => i.compare !== undefined) ? 34 : 24, labelW = 200, barW = W - labelW - 90;
  const bars = data.items.map((item, i) => {
    const y = 10 + i * rowH, w = data.max ? (item.value / data.max) * barW : 0, cw = item.compare !== undefined && data.max ? (item.compare / data.max) * barW : 0;
    const x = rtl ? W - labelW - 8 - w : labelW + 8, cx = rtl ? W - labelW - 8 - cw : labelW + 8;
    return `<text x="${rtl ? W - 4 : labelW}" y="${y + 13}" font-size="12" text-anchor="end">${esc(item.label.slice(0, 32))}</text>`
      + `<rect x="${x.toFixed(1)}" y="${y + 2}" width="${Math.max(w, 1).toFixed(1)}" height="14" rx="3" fill="${item.tone ? TONE_COLORS[item.tone] : CHART_COLORS[0]}"/>`
      + `<text x="${(rtl ? x - 6 : x + w + 6).toFixed(1)}" y="${y + 13}" font-size="11" text-anchor="${rtl ? 'end' : 'start'}">${esc(format(item.value))}</text>`
      + (item.compare !== undefined ? `<rect x="${cx.toFixed(1)}" y="${y + 19}" width="${Math.max(cw, 1).toFixed(1)}" height="9" rx="3" fill="#94a3b8"/><text x="${(rtl ? cx - 6 : cx + cw + 6).toFixed(1)}" y="${y + 27}" font-size="10" fill="#64748b" text-anchor="${rtl ? 'end' : 'start'}">${esc(format(item.compare))}</text>` : '');
  }).join('');
  const legend = data.compareLabel ? `<g font-size="11" fill="#475569"><rect x="${labelW + 8}" y="${10 + data.items.length * rowH + 4}" width="10" height="10" rx="2" fill="${CHART_COLORS[0]}"/><text x="${labelW + 22}" y="${10 + data.items.length * rowH + 13}">${esc(data.valueLabel || '')}</text><rect x="${labelW + 180}" y="${10 + data.items.length * rowH + 4}" width="10" height="10" rx="2" fill="#94a3b8"/><text x="${labelW + 194}" y="${10 + data.items.length * rowH + 13}">${esc(data.compareLabel)}</text></g>` : '';
  const h = 20 + data.items.length * rowH + (legend ? 24 : 0);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${h}" viewBox="0 0 ${W} ${h}">${bars}${legend}</svg>`;
}
